import dayjs from "dayjs";
import { MARKET_LABELS } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import Odds from "@/components/Odds";
import { UnitsHeading } from "@/components/UnitsHeading";

const SKELETONS = ["s1", "s2", "s3", "s4", "s5"];

function TopStat({ label, value, tone = "green", testid }) {
  const tones = { green: "text-[#00E676]", red: "text-[#EF4444]", white: "text-white" };
  return (
    <div className="card-mp p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className={`mt-2 font-mono text-2xl font-bold tracking-tight sm:text-3xl ${tones[tone]}`}>{value}</p>
    </div>
  );
}

const signed = (v, suffix) => `${v > 0 ? "+" : ""}${v}${suffix}`;
export const aud = (v) => `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedAud = (v) => `${v > 0 ? "+" : ""}${aud(v)}`;

export function ResultsStats({ overall: o, isLoading }) {
  if (isLoading || !o) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {SKELETONS.map((k) => <Skeleton key={k} className="h-24 rounded-xl bg-[#141A26]" />)}
      </div>
    );
  }
  const hasAud = o.staked_aud != null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <TopStat label={`${o.season || "Season"} Record`} value={o.record} testid="results-record" />
        <TopStat label="Win %" value={`${o.win_pct}%`} testid="results-win-pct" />
        <TopStat label={<UnitsHeading label="Net Units" />} value={signed(o.net_units, "U")} tone={o.net_units >= 0 ? "green" : "red"} testid="results-units" />
        <TopStat label="ROI" value={signed(o.roi, "%")} tone={o.roi >= 0 ? "green" : "red"} testid="results-roi" />
        <TopStat label="Avg Odds" value={<Odds value={o.avg_odds} />} tone="white" testid="results-avg-odds" />
      </div>
      {hasAud && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="results-aud-stats">
          <TopStat label="Net Profit (AUD)" value={signedAud(o.net_aud)} tone={o.net_aud >= 0 ? "green" : "red"} testid="results-net-aud" />
          <TopStat label="Total Staked (AUD)" value={aud(o.staked_aud)} tone="white" testid="results-staked-aud" />
          <TopStat label="Total Returns (AUD)" value={aud(o.returns_aud)} tone="white" testid="results-returns-aud" />
          <div className="card-mp p-5" data-testid="results-bet-count">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recorded Bets</p>
            <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-white sm:text-3xl">{o.total_bets}</p>
            <p className="mt-1 font-mono text-[10px] text-slate-500">{o.historical_count} manually recorded · {o.website_count} website pick{o.website_count === 1 ? "" : "s"} · 1U = ${o.unit_stake_aud} AUD</p>
          </div>
        </div>
      )}
    </div>
  );
}

function PerfRow({ label, record, netUnits }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-[#1E2738] bg-[#0F131D] px-4 py-3">
      <span className="text-sm font-semibold text-slate-200">{label}</span>
      <div className="flex items-center gap-4 font-mono text-xs">
        <span className="text-slate-400">{record}</span>
        <span className={netUnits >= 0 ? "font-bold text-[#00E676]" : "font-bold text-[#EF4444]"}>{signed(netUnits, "U")}</span>
      </div>
    </div>
  );
}

export function PerformanceBreakdown({ analytics }) {
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-2">
      <div className="card-mp p-5 sm:p-6" data-testid="market-performance">
        <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Market Performance</h3>
        <div className="mt-4 space-y-3">
          {Object.entries(analytics?.by_market || {}).map(([m, s]) => (
            <PerfRow key={m} label={MARKET_LABELS[m] || m} record={s.record} netUnits={s.net_units} />
          ))}
        </div>
      </div>
      <div className="card-mp p-5 sm:p-6" data-testid="monthly-performance">
        <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Monthly Performance</h3>
        <div className="mt-4 space-y-3">
          {(analytics?.monthly || []).map((m) => (
            <PerfRow key={m.month} label={dayjs(`${m.month}-01`).format("MMMM YYYY")} record={m.record} netUnits={m.net_units} />
          ))}
        </div>
      </div>
    </div>
  );
}
